<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ContactMergeDuplicateModal from 'dashboard/components-next/Contacts/WahaDuplicates/ContactMergeDuplicateModal.vue';

// SiteUp (Int-F4): dados públicos do WhatsApp que o job de enriquecimento
// (Waha::ContactEnrichmentJob, best-effort e assíncrono) grava em
// contact.additional_attributes. Somente leitura — nada aqui é editável,
// em contraste direto com "Atributos do contato" logo abaixo, que tem
// lápis/lixeira. Mockup aprovado: intf4-variants/01-sidebar-enriquecimento.html.
const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const STALE_AFTER_DAYS = 30;

const attrs = computed(() => props.contact?.additional_attributes || {});
const wahaStatus = computed(() => attrs.value.waha_status || '');
const wahaDeviceCount = computed(() => attrs.value.waha_device_count ?? null);
const wahaEnrichedAt = computed(() => attrs.value.waha_enriched_at || null);
const wahaBusiness = computed(() => Boolean(attrs.value.waha_business));
const wahaBusinessAddress = computed(
  () => attrs.value.waha_business_address || ''
);
const wahaBusinessEmail = computed(() => attrs.value.waha_business_email || '');

const hasAnyWahaData = computed(
  () =>
    Boolean(wahaStatus.value) ||
    wahaBusiness.value ||
    Boolean(wahaBusinessAddress.value) ||
    Boolean(wahaBusinessEmail.value)
);

const enrichedDate = computed(() =>
  wahaEnrichedAt.value ? new Date(wahaEnrichedAt.value) : null
);

const daysSinceEnriched = computed(() => {
  if (!enrichedDate.value) return null;
  const diffMs = Date.now() - enrichedDate.value.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
});

const isStale = computed(
  () =>
    daysSinceEnriched.value !== null &&
    daysSinceEnriched.value > STALE_AFTER_DAYS
);

// "Verificado hoje, 09:41" · "Verificado em 09/05 — há 3 meses."
const freshnessText = computed(() => {
  if (!enrichedDate.value) return '';
  const days = daysSinceEnriched.value;
  const timeStr = enrichedDate.value.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (days === 0) {
    return t('CONVERSATION_SIDEBAR.WAHA_INFO.FRESHNESS_TODAY', {
      time: timeStr,
    });
  }

  const dateStr = enrichedDate.value.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
  const agoStr =
    days < 60
      ? t('CONVERSATION_SIDEBAR.WAHA_INFO.DAYS_AGO', { count: days })
      : t('CONVERSATION_SIDEBAR.WAHA_INFO.MONTHS_AGO', {
          count: Math.floor(days / 30),
        });

  return isStale.value
    ? t('CONVERSATION_SIDEBAR.WAHA_INFO.FRESHNESS_STALE', {
        date: dateStr,
        ago: agoStr,
      })
    : t('CONVERSATION_SIDEBAR.WAHA_INFO.FRESHNESS_RECENT', {
        date: dateStr,
        ago: agoStr,
      });
});

// ---- Verificar agora ----
const isVerifying = ref(false);

const verificarAgora = async () => {
  if (isVerifying.value || !props.contact?.id) return;
  isVerifying.value = true;
  try {
    const { data } = await ContactAPI.wahaEnrich(props.contact.id);
    if (data?.throttled) {
      useAlert(t('CONVERSATION_SIDEBAR.WAHA_INFO.VERIFY_THROTTLED'));
    } else {
      // Best-effort e assíncrono — não finge que o dado já chegou.
      useAlert(t('CONVERSATION_SIDEBAR.WAHA_INFO.VERIFY_REQUESTED'));
    }
  } catch (error) {
    useAlert(t('CONVERSATION_SIDEBAR.WAHA_INFO.VERIFY_ERROR'));
  } finally {
    isVerifying.value = false;
  }
};

// ---- Duplicata por LID ----
const duplicateGroup = ref(null);
const isDismissedThisSession = ref(false);
const showMergeModal = ref(false);

const otherContactInGroup = computed(() => {
  if (!duplicateGroup.value || !props.contact?.id) return null;
  return (
    duplicateGroup.value.contacts.find(c => c.id !== props.contact.id) || null
  );
});

const showDuplicateAlert = computed(
  () => Boolean(otherContactInGroup.value) && !isDismissedThisSession.value
);

const fetchDuplicateGroup = async () => {
  duplicateGroup.value = null;
  if (!props.contact?.id || !attrs.value.waha_lid) return;
  try {
    const { data } = await ContactAPI.getWahaDuplicates(props.contact.id);
    duplicateGroup.value = data?.payload?.[0] || null;
  } catch (error) {
    // Falha ao checar duplicata não pode quebrar o resto do painel — o
    // bloco de dados do WhatsApp continua funcionando normalmente.
    duplicateGroup.value = null;
  }
};

const dismissDuplicate = () => {
  // SiteUp: persistir essa decisão (não voltar a avisar) é a fatia IF4-C
  // (tela de revisão em lote) — aqui é só sessão, propositalmente. Ver
  // tradeoff documentado no mockup 01.
  isDismissedThisSession.value = true;
};

watch(
  () => props.contact?.id,
  () => {
    isDismissedThisSession.value = false;
    fetchDuplicateGroup();
  }
);

onMounted(fetchDuplicateGroup);
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="contact-waha-info">
    <div
      v-if="showDuplicateAlert"
      class="rounded-xl border border-n-amber-6 bg-n-amber-2 px-3 py-2.5"
      data-testid="waha-duplicate-alert"
    >
      <div
        class="flex items-center gap-1.5 text-xs font-semibold text-n-amber-11"
      >
        <span class="size-2 rounded-full bg-n-amber-9 shrink-0" />
        {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.DUPLICATE.TITLE') }}
      </div>
      <p class="mt-1 text-xs text-n-slate-12">
        {{
          t('CONVERSATION_SIDEBAR.WAHA_INFO.DUPLICATE.BODY', {
            name: otherContactInGroup.name,
            phone: otherContactInGroup.phone_number,
            count: otherContactInGroup.conversations_count,
          })
        }}
      </p>
      <div class="mt-2 flex gap-2">
        <button
          type="button"
          class="rounded-full border border-n-weak px-3 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="waha-duplicate-compare"
          @click="showMergeModal = true"
        >
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.DUPLICATE.COMPARE') }}
        </button>
        <button
          type="button"
          class="rounded-full border border-n-weak px-3 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
          data-testid="waha-duplicate-dismiss"
          @click="dismissDuplicate"
        >
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.DUPLICATE.DISMISS') }}
        </button>
      </div>
    </div>

    <div
      class="rounded-2xl border border-n-weak bg-n-solid-1 px-3 py-2.5"
      data-testid="waha-info-block"
    >
      <div class="flex items-center gap-1.5">
        <span class="i-ri-whatsapp-line size-4 shrink-0 text-n-slate-11" />
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.TITLE') }}
        </h3>
        <span
          v-if="hasAnyWahaData"
          class="ml-auto inline-flex items-center gap-1 rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-n-slate-11"
        >
          <span class="i-ri-lock-line size-3" />
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.READ_ONLY') }}
        </span>
      </div>

      <template v-if="hasAnyWahaData">
        <div class="mt-2 flex flex-col gap-1.5">
          <div
            v-if="wahaBusiness"
            class="flex items-start gap-2 text-xs text-n-slate-12"
          >
            <span
              class="i-ri-briefcase-line size-3.5 shrink-0 mt-0.5 text-n-slate-10"
            />
            <span
              ><b class="font-medium">{{
                t('CONVERSATION_SIDEBAR.WAHA_INFO.BUSINESS_ACCOUNT')
              }}</b></span
            >
          </div>
          <div
            v-if="wahaStatus"
            class="flex items-start gap-2 text-xs text-n-slate-11"
          >
            <span
              class="i-ri-chat-quote-line size-3.5 shrink-0 mt-0.5 text-n-slate-10"
            />
            <i
              class="not-italic before:content-['\201C'] after:content-['\201D']"
              >{{ wahaStatus }}</i
            >
          </div>
          <div
            v-if="wahaBusinessAddress"
            class="flex items-start gap-2 text-xs text-n-slate-12"
          >
            <span
              class="i-ri-map-pin-line size-3.5 shrink-0 mt-0.5 text-n-slate-10"
            />
            <span>{{ wahaBusinessAddress }}</span>
          </div>
          <div
            v-if="wahaBusinessEmail"
            class="flex items-start gap-2 text-xs text-n-slate-12"
          >
            <span
              class="i-ri-mail-line size-3.5 shrink-0 mt-0.5 text-n-slate-10"
            />
            <span>{{ wahaBusinessEmail }}</span>
          </div>
        </div>
        <div
          class="mt-2 flex items-center gap-1.5 border-t border-n-weak pt-2 text-[11px] text-n-slate-10"
        >
          <span
            v-if="isStale"
            class="size-1.5 rounded-full bg-n-amber-9 shrink-0"
          />
          <span class="i-ri-time-line size-3 shrink-0" />
          <span class="min-w-0 flex-1">
            {{ freshnessText
            }}<template v-if="wahaDeviceCount">
              ·
              {{
                t('CONVERSATION_SIDEBAR.WAHA_INFO.DEVICE_COUNT', {
                  count: wahaDeviceCount,
                })
              }}</template
            >
          </span>
          <button
            type="button"
            class="flex size-6 shrink-0 items-center justify-center rounded-md hover:bg-n-alpha-2 disabled:opacity-50"
            :aria-label="t('CONVERSATION_SIDEBAR.WAHA_INFO.VERIFY_NOW')"
            :title="t('CONVERSATION_SIDEBAR.WAHA_INFO.VERIFY_NOW')"
            data-testid="waha-verify-now"
            :disabled="isVerifying"
            @click="verificarAgora"
          >
            <span
              class="i-ri-refresh-line size-3.5"
              :class="{ 'animate-spin': isVerifying }"
            />
          </button>
        </div>
      </template>

      <template v-else>
        <p class="mt-2 text-xs text-n-slate-11">
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.EMPTY') }}
        </p>
        <button
          type="button"
          class="mt-2 inline-flex items-center gap-1.5 rounded-full border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          data-testid="waha-verify-now-empty"
          :disabled="isVerifying"
          @click="verificarAgora"
        >
          <span
            class="i-ri-refresh-line size-3.5"
            :class="{ 'animate-spin': isVerifying }"
          />
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.VERIFY_NOW') }}
        </button>
      </template>
    </div>

    <ContactMergeDuplicateModal
      v-if="showMergeModal && duplicateGroup && otherContactInGroup"
      v-model:show="showMergeModal"
      :group="duplicateGroup"
      @merged="dismissDuplicate"
    />
  </div>
</template>
